import { OPMFATData } from "@/api/opm-fat";
import { OPMFDTData } from "@/api/opm-fdt";
import { NNEvidenceData } from "@/api/nn-evidence";
import { GPONData } from "@/api/gpon";
import { HPTARData, HPTARInvalidData, InfiniteKmzInvalids } from "@/api/hptar";
import { RFSData } from "@/api/rfs";
import { STATUS } from "@/utils/constant";
import { SubmitSupportingDocumentsFormSchema } from "./useDocumentPrepare";
import {
  CODE_META,
  SupportingDocumentField,
} from "./useCheckingProfileValidations";
import { UseFormReturn } from "react-hook-form";
import { SupportingDocumentData } from "@/api/supporting-document";

export function isOPMValidated({
  opmFdts,
  opmFats,
  abdFdtCount,
  abdFatCount,
}: {
  opmFdts: OPMFDTData[] | undefined;
  opmFats: OPMFATData[] | undefined;
  abdFdtCount: number | undefined;
  abdFatCount: number | undefined;
}) {
  return (
    opmFdts?.length === 1 &&
    opmFdts[0].valid &&
    !opmFdts[0].revise &&
    opmFats?.every((opmFat) => opmFat.valid) &&
    opmFdts.length === abdFdtCount &&
    opmFats.length === abdFatCount
  );
}

export function isNNEvidenceValidated({
  nnEvidences,
}: {
  nnEvidences: NNEvidenceData[] | undefined;
}) {
  const nnEvidence = nnEvidences?.[0];
  const isValidated =
    nnEvidence?.valid &&
    !nnEvidence?.revise &&
    nnEvidence?.statusValidation ===
      (STATUS.VALIDATION.SUCCESS || STATUS.VALIDATION.FAILED);

  return isValidated;
}

export function isGPONValidated({ gpons }: { gpons: GPONData[] | undefined }) {
  return gpons?.every((gpon) => gpon.valid && !gpon.revise);
}

export function isHPTARValidated({
  hptars,
  hptarInvalids = [],
  hptarGeneralMessages = [],
  kmzInvalids = [],
}: {
  hptars: HPTARData[] | undefined;
  hptarInvalids?: HPTARInvalidData[];
  hptarGeneralMessages?: { id: string; message: string }[];
  kmzInvalids?: InfiniteKmzInvalids["content"];
}) {
  const hptar = hptars?.[0];
  return (
    hptar?.valid && // HPTAR harus valid
    !hptar?.revise && // HPTAR tidak boleh dalam kondisi revise
    hptars?.every((hptar) => hptar.valid && !hptar.revise) && // Semua HPTAR harus valid dan tidak dalam kondisi revise
    hptarInvalids.length === 0 && // Tidak boleh ada invalids
    hptarGeneralMessages.length === 0 && // Tidak boleh ada general messages
    kmzInvalids.length === 0 // Tidak boleh ada KMZ invalids
  );
}

export function getStatusValidation(
  isValid?: boolean,
  isRevise?: boolean,
  statusValidation?: string,
) {
  if (!isValid && statusValidation === STATUS.VALIDATION.IN_PROGRESS) {
    return STATUS.DOCUMENT.VALIDATING;
  } else if (
    isValid &&
    !isRevise &&
    statusValidation === (STATUS.VALIDATION.SUCCESS || STATUS.VALIDATION.FAILED)
  ) {
    return STATUS.DOCUMENT.VALIDATED;
  } else if (statusValidation === STATUS.VALIDATION.FAILED) {
    return STATUS.DOCUMENT.FAILED;
  } else {
    return STATUS.DOCUMENT.UNVALIDATED;
  }
}

export function handleValidationFields(
  values: SubmitSupportingDocumentsFormSchema,
  fields: SupportingDocumentField[],
  form: UseFormReturn<SubmitSupportingDocumentsFormSchema>,
  supportingDocument?: SupportingDocumentData,
  isRejectFlow = false,
) {
  // Clear all errors first before validating
  form.clearErrors();

  // Array untuk menyimpan field yang bermasalah
  const problematicFields: string[] = [];

  // Iterasi melalui setiap field untuk memvalidasi
  fields.forEach((field) => {
    // Periksa apakah field ini required now
    if (field.isRequired !== true) {
      return;
    }

    // Periksa apakah value dari field yang selaras kosong/undefined
    const fieldValue = values[field.code as keyof typeof CODE_META];

    const meta = CODE_META[field.code as keyof typeof CODE_META];
    const reviseKey = `${meta.doc}IsRevise` as keyof SupportingDocumentData;
    const isRevise = supportingDocument?.[reviseKey] === true;

    const isRequired = isRejectFlow ? isRevise : true;

    // Dokumen yang sudah pernah ke-upload (ada di GET supporting-document)
    // dianggap terisi walau belum disentuh di sesi ini — kecuali kalau
    // reviewer memang minta direvisi (isRevise), file lama tidak boleh
    // "meloloskan" validasi, wajib upload ulang.
    const pathKey = `${meta.doc}Path` as keyof SupportingDocumentData;
    const hasExistingDocument = Boolean(supportingDocument?.[pathKey]);
    const satisfiesExisting =
      hasExistingDocument && !(isRejectFlow && isRevise);

    // Field dianggap kosong jika undefined atau null
    if (!fieldValue?.id && !satisfiesExisting && isRequired) {
      problematicFields.push(field.code);

      // Set error for the specific field
      form.setError(
        // @ts-expect-error mismatch type
        `${field.code}.id`,
        {
          type: "required",
          message: `Required`,
        },
      );
    }
  });

  // Return validation result
  return problematicFields.length === 0; // Indikasi validasi berhasil jika tidak ada field yang bermasalah
}

// Helper types for validation rules
type ValidationStatus =
  | "Validated"
  | "Partially Validated"
  | "Unvalidated"
  | "Validation Bypassed"
  | "Optional";

interface ValidationRule {
  isSubmitted: boolean | null;
  status: ValidationStatus;
  completedDateRequired: boolean | null; // null means don't check
  uncompletedDateRequired: boolean | null; // null means don't check
}

// Validation rules configuration
const VALIDATION_RULES: ValidationRule[] = [
  // Case 1: Submitted AND Validated AND Completed Date IS NOT NULL
  {
    isSubmitted: true,
    status: "Validated",
    completedDateRequired: true,
    uncompletedDateRequired: null,
  },

  // Case 2: Submitted AND Partially Validated AND Uncompleted Date IS NOT NULL
  {
    isSubmitted: true,
    status: "Partially Validated",
    completedDateRequired: null,
    uncompletedDateRequired: true,
  },

  // Case 3: Submitted AND Unvalidated AND Uncompleted Date IS NOT NULL
  {
    isSubmitted: true,
    status: "Unvalidated",
    completedDateRequired: null,
    uncompletedDateRequired: true,
  },

  // Case 4: Not Submitted AND Unvalidated AND Uncompleted Date IS NOT NULL AND Completed Date IS NULL
  {
    isSubmitted: false,
    status: "Unvalidated",
    completedDateRequired: false,
    uncompletedDateRequired: true,
  },

  // Case 5: Submitted AND Validation Bypassed AND Completed Date IS NOT NULL
  {
    isSubmitted: true,
    status: "Validation Bypassed",
    completedDateRequired: true,
    uncompletedDateRequired: null,
  },

  // Case 6: Submitted AND Optional AND Uncompleted Date IS NOT NULL AND Completed Date IS NULL
  {
    isSubmitted: true,
    status: "Optional",
    completedDateRequired: false,
    uncompletedDateRequired: true,
  },

  // Case 7: Submitted AND Optional AND Completed Date IS NOT NULL
  {
    isSubmitted: true,
    status: "Optional",
    completedDateRequired: true,
    uncompletedDateRequired: null,
  },

  // Case 8: Submitted AND Validation Bypassed AND Completed Date IS NULL AND Uncompleted Date IS NOT NULL (to cover any edge case where both dates are filled)
  {
    isSubmitted: true,
    status: "Validation Bypassed",
    completedDateRequired: null,
    uncompletedDateRequired: true,
  },
];

// Helper function to check if a validation rule matches the current state
function matchesValidationRule(
  rule: ValidationRule,
  isSubmitted: boolean | null,
  status: string | null,
  completedDate: string | null,
  uncompletedDate: string | null,
): boolean {
  // Check submission status
  if (rule.isSubmitted !== isSubmitted) return false;

  // Check status
  if (rule.status !== status) return false;

  // Check completed date requirement
  if (rule.completedDateRequired !== null) {
    const hasCompletedDate = completedDate !== null;
    if (rule.completedDateRequired !== hasCompletedDate) return false;
  }

  // Check uncompleted date requirement
  if (rule.uncompletedDateRequired !== null) {
    const hasUncompletedDate = uncompletedDate !== null;
    if (rule.uncompletedDateRequired !== hasUncompletedDate) return false;
  }

  return true;
}

// Generic validation function that works for both OPM and Evidence NN
function validateByRFSData(
  isSubmitted: boolean | null,
  status: string | null,
  completedDate: string | null,
  uncompletedDate: string | null,
): boolean {
  return VALIDATION_RULES.some((rule) =>
    matchesValidationRule(
      rule,
      isSubmitted,
      status,
      completedDate,
      uncompletedDate,
    ),
  );
}

// New RFS-based validation functions based on business rules
export function isOPMValidatedByRFS(rfs: RFSData | undefined): boolean {
  if (!rfs) return false;

  const { opmStatus, opmIsSubmitted, opmCompletedDate, opmUncompletedDate } =
    rfs;

  return validateByRFSData(
    opmIsSubmitted,
    opmStatus,
    opmCompletedDate,
    opmUncompletedDate,
  );
}

export function isNNEvidenceValidatedByRFS(rfs: RFSData | undefined): boolean {
  if (!rfs) return false;

  const {
    evidenceNnStatus,
    evidenceNnIsSubmitted,
    evidenceNnCompletedDate,
    evidenceNnUncompletedDate,
  } = rfs;

  return validateByRFSData(
    evidenceNnIsSubmitted,
    evidenceNnStatus,
    evidenceNnCompletedDate,
    evidenceNnUncompletedDate,
  );
}

export function isGPONValidatedByRFS(rfs: RFSData | undefined): boolean {
  if (!rfs) return false;

  const validGPONStatuses: ValidationStatus[] = [
    "Validated",
    "Validation Bypassed",
    "Partially Validated",
    "Optional",
  ];

  return validGPONStatuses.includes(rfs.gponStatus as ValidationStatus);
}

export function showDummyHouseNumbersAlert(nnEvidence?: NNEvidenceData) {
  return nnEvidence?.valid && nnEvidence?.documentNnEvidencePath == null;
}
